export const makeAuthHeaders = (token) => ({ Authorization: `Bearer ${token}` });

export async function fetchAdminDashboard(apiBase, token) {
  const authHeaders = makeAuthHeaders(token);
  const [overviewResponse, usersResponse, auditResponse, agentResponse, proposalResponse, patchResponse, deployResponse] = await Promise.all([
    fetch(`${apiBase}/admin/overview`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/users`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/audit-logs?limit=20`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/agent/tasks?limit=30`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/code-proposals?limit=8`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/code-patches?limit=8`, { method: "GET", headers: authHeaders }),
    fetch(`${apiBase}/admin/deploy/history?limit=10`, { method: "GET", headers: authHeaders }),
  ]);

  const overviewData = await overviewResponse.json().catch(() => ({}));
  const usersData = await usersResponse.json().catch(() => ({}));
  const auditData = await auditResponse.json().catch(() => ({}));
  const agentData = await agentResponse.json().catch(() => ({}));
  const proposalData = await proposalResponse.json().catch(() => ({}));
  const patchData = await patchResponse.json().catch(() => ({}));
  const deployData = await deployResponse.json().catch(() => ({}));

  if (!overviewResponse.ok) throw new Error(overviewData.detail || "관리자 대시보드를 불러오지 못했습니다.");
  if (!usersResponse.ok) throw new Error(usersData.detail || "회원 목록을 불러오지 못했습니다.");

  return {
    overview: overviewData,
    users: usersData.users || [],
    auditLogs: auditResponse.ok ? (auditData.logs || []) : [],
    agentTasks: agentResponse.ok ? (agentData.tasks || []) : [],
    agentStatus: agentResponse.ok ? (agentData.status || "success") : "error",
    agentError: agentResponse.ok ? (agentData.error || "") : "Agent 작업 목록을 불러오지 못했습니다.",
    codeProposals: proposalResponse.ok ? (proposalData.proposals || []) : [],
    codePatches: patchResponse.ok ? (patchData.patches || []) : [],
    deployRuns: deployResponse.ok ? (deployData.runs || []) : [],
  };
}

export async function updateAdminUser(apiBase, token, targetUserid, payload) {
  const response = await fetch(`${apiBase}/admin/users/${encodeURIComponent(targetUserid)}`, {
    method: "PATCH",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "회원 정보 수정에 실패했습니다.");
  return data;
}

export async function fetchAdminUsers(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/users`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "직원 목록을 불러오지 못했습니다.");
  return data;
}

export async function createAdminUser(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/users`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "직원 계정 생성에 실패했습니다.");
  return data;
}

export async function approveAgentTask(apiBase, token, taskId, approve) {
  const response = await fetch(`${apiBase}/admin/agent/tasks/${encodeURIComponent(taskId)}/approve`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ approve, note: approve ? "dashboard approved" : "dashboard rejected" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "AI 작업 승인 처리에 실패했습니다.");
  return data;
}

export async function runAgentDocumentTask(apiBase, token, file, instruction) {
  const formData = new FormData();
  if (file) formData.append("file", file);
  formData.append("instruction", instruction || "");

  const response = await fetch(`${apiBase}/admin/agent/document-task`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "문서 AI 작업 실행에 실패했습니다.");
  return data;
}

export async function createCodeProposal(apiBase, token, file, instruction) {
  const formData = new FormData();
  if (file) formData.append("file", file);
  formData.append("instruction", instruction || "");

  const response = await fetch(`${apiBase}/admin/code-proposals`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "코드 수정 제안서 생성에 실패했습니다.");
  return data;
}

export async function fetchServerStatus(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/server/status`, {
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 상태를 불러오지 못했습니다.");
  return data;
}

export async function fetchDevConsoleRoots(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/dev-console/roots`, {
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "개발 콘솔 루트를 불러오지 못했습니다.");
  return data;
}

export async function listDevConsoleDir(apiBase, token, path = "/home/tobe-project") {
  const response = await fetch(`${apiBase}/admin/dev-console/list?path=${encodeURIComponent(path)}`, {
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 파일 목록을 불러오지 못했습니다.");
  return data;
}

export async function readDevConsoleFile(apiBase, token, path) {
  const response = await fetch(`${apiBase}/admin/dev-console/file?path=${encodeURIComponent(path)}`, {
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 파일을 읽지 못했습니다.");
  return data;
}

export async function writeDevConsoleFile(apiBase, token, path, content) {
  const response = await fetch(`${apiBase}/admin/dev-console/file`, {
    method: "PATCH",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ path, content, confirm_text: "SAVE" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 파일 저장에 실패했습니다.");
  return data;
}

export async function runDevConsoleCommand(apiBase, token, cwd, command) {
  const response = await fetch(`${apiBase}/admin/dev-console/command`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ cwd, command, confirm_text: "RUN" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 명령 실행에 실패했습니다.");
  return data;
}

export async function runServerAction(apiBase, token, action) {
  const response = await fetch(`${apiBase}/admin/server/action`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ action, confirm: "SERVER" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 작업 실행에 실패했습니다.");
  return data;
}

export async function fetchOpsServers(apiBase, token) {
  const response = await fetch(`${apiBase}/workspace/ops/servers`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 접속정보를 불러오지 못했습니다.");
  return data;
}

export async function saveOpsServer(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/workspace/ops/servers`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 접속정보 저장에 실패했습니다.");
  return data;
}

export async function runOpsHealth(apiBase, token, targetId, serverKey) {
  const response = await fetch(`${apiBase}/workspace/ops/health`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ target_id: targetId, server_key: serverKey, explain: false }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 헬스체크에 실패했습니다.");
  return data;
}

export async function fetchOpsServiceMap(apiBase, token) {
  const response = await fetch(`${apiBase}/workspace/ops/service-map`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 서비스 매핑을 불러오지 못했습니다.");
  return data;
}

export async function runOpsInventory(apiBase, token, targetId, serverKey) {
  const response = await fetch(`${apiBase}/workspace/ops/inventory`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ target_id: targetId, server_key: serverKey }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 인벤토리 수집에 실패했습니다.");
  return data;
}

export async function fetchOpsHealthHistory(apiBase, token, targetKey, limit = 30) {
  const url = `${apiBase}/workspace/ops/health/history?target_key=${encodeURIComponent(targetKey)}&limit=${encodeURIComponent(limit)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 헬스체크 이력을 불러오지 못했습니다.");
  return data;
}

export async function fetchTiaExternalControl(apiBase, token) {
  const response = await fetch(`${apiBase}/workspace/tia/external-control`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "TIA 외부 호출 설정을 불러오지 못했습니다.");
  return data;
}

export async function updateTiaExternalControl(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/workspace/tia/external-control`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "TIA 외부 호출 설정 저장에 실패했습니다.");
  return data;
}

export async function runOpsExec(apiBase, token, targetId, command) {
  const response = await fetch(`${apiBase}/workspace/ops/exec`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ target_id: targetId, command }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "서버 점검 명령 실행에 실패했습니다.");
  return data;
}

export async function fetchCodexProjectMap(apiBase, token, query = "") {
  const url = `${apiBase}/admin/codex/project-map?limit=80${query ? `&query=${encodeURIComponent(query)}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 프로젝트 맵을 불러오지 못했습니다.");
  return data;
}

export async function createCodexTaskPlan(apiBase, token, instruction) {
  const response = await fetch(`${apiBase}/admin/codex/task-plan`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ instruction }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 작업 계획 생성에 실패했습니다.");
  return data;
}

export async function createCodexAppBlueprint(apiBase, token, conversation, projectName = "TOBE App") {
  const response = await fetch(`${apiBase}/admin/codex/app-blueprint`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ conversation, project_name: projectName }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 앱 설계 생성에 실패했습니다.");
  return data;
}

export async function analyzeCodexSourceZip(apiBase, token, file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${apiBase}/admin/codex/source-zip`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 소스 압축파일 분석에 실패했습니다.");
  return data;
}

export async function analyzeCodexReferenceImage(apiBase, token, file, instruction = "") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("instruction", instruction || "");
  const response = await fetch(`${apiBase}/admin/codex/reference-image`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 참고 이미지 분석에 실패했습니다.");
  return data;
}

export async function analyzeCodexPlanningDocument(apiBase, token, file, instruction = "") {
  const formData = new FormData();
  if (file) formData.append("file", file);
  formData.append("instruction", instruction || "");
  const response = await fetch(`${apiBase}/admin/codex/planning-document`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "투비 코드 기획서 분석에 실패했습니다.");
  return data;
}

export async function decideCodeProposal(apiBase, token, proposalId, approve) {
  const response = await fetch(`${apiBase}/admin/code-proposals/${encodeURIComponent(proposalId)}/decision`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ approve }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "코드 제안 승인 처리에 실패했습니다.");
  return data;
}

export async function deleteCodeProposal(apiBase, token, proposalId) {
  const response = await fetch(`${apiBase}/admin/code-proposals/${encodeURIComponent(proposalId)}`, {
    method: "DELETE",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "코드 제안 삭제에 실패했습니다.");
  return data;
}

export async function createCodePatch(apiBase, token, proposalId) {
  const response = await fetch(`${apiBase}/admin/code-proposals/${encodeURIComponent(proposalId)}/patch`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 초안 생성에 실패했습니다.");
  return data;
}

export async function deleteCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}`, {
    method: "DELETE",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 삭제에 실패했습니다.");
  return data;
}

export async function regenerateCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/regenerate`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 재생성에 실패했습니다.");
  return data;
}

export async function prepareCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/prepare`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 준비에 실패했습니다.");
  return data;
}

export async function dryRunCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/dry-run`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 모의 실행에 실패했습니다.");
  return data;
}

export async function applyCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/apply`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: "APPLY" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 적용에 실패했습니다.");
  return data;
}

export async function verifyCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/verify`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "패치 검증에 실패했습니다.");
  return data;
}

export async function restoreCodePatch(apiBase, token, patchId) {
  const response = await fetch(`${apiBase}/admin/code-patches/${encodeURIComponent(patchId)}/restore`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: "RESTORE" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "백업 복구에 실패했습니다.");
  return data;
}

export async function runDeployCheck(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/deploy/check`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "배포 검사에 실패했습니다.");
  return data;
}

export async function validateDeployZip(apiBase, token, file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${apiBase}/admin/deploy/validate-zip`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "배포 압축파일 사전 검증에 실패했습니다.");
  return data;
}

export async function uploadDeployZip(apiBase, token, file, validationId = "") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("validation_id", validationId);
  const response = await fetch(`${apiBase}/admin/deploy/upload-zip`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "배포 압축파일 업로드에 실패했습니다.");
  return data;
}

export async function runFrontendBuild(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/deploy/frontend-build`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "프론트 빌드에 실패했습니다.");
  return data;
}

export async function restartBackend(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/deploy/restart-backend`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: "RESTART" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "백엔드 재시작 요청에 실패했습니다.");
  return data;
}

export async function runSafeDeploy(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/deploy/safe-run`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: "DEPLOY" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "안전 배포에 실패했습니다.");
  return data;
}

export async function fetchKnowledgeItems(apiBase, token, { mode = "general", query = "", limit = 80 } = {}) {
  const params = new URLSearchParams({ mode, query, limit: String(limit) });
  const response = await fetch(`${apiBase}/admin/knowledge?${params.toString()}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "지식베이스 목록을 불러오지 못했습니다.");
  return data;
}

export async function addKnowledgeText(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/knowledge/text`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "지식 등록에 실패했습니다.");
  return data;
}

export async function addKnowledgeFile(apiBase, token, file, mode = "general", visibility = "company") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mode", mode);
  formData.append("visibility", visibility);
  const response = await fetch(`${apiBase}/admin/knowledge/file`, {
    method: "POST",
    headers: makeAuthHeaders(token),
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "파일 지식 등록에 실패했습니다.");
  return data;
}

export async function deleteKnowledgeItem(apiBase, token, itemId, mode = "general") {
  const response = await fetch(`${apiBase}/admin/knowledge/${encodeURIComponent(itemId)}?mode=${encodeURIComponent(mode)}`, {
    method: "DELETE",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "지식 삭제에 실패했습니다.");
  return data;
}

// 학습형 지식 검토 큐: 채팅이 웹에서 자동 학습한 지식(pending)을 사람이 승인/거절
export async function fetchLearnedPending(apiBase, token, { limit = 50 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  const response = await fetch(`${apiBase}/admin/knowledge/learned?${params.toString()}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "학습 지식 검토 큐를 불러오지 못했습니다.");
  return data;
}

export async function setLearnedStatus(apiBase, token, itemId, status) {
  const response = await fetch(`${apiBase}/admin/knowledge/learned/${encodeURIComponent(itemId)}/status`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "학습 지식 상태를 변경하지 못했습니다.");
  return data;
}

export async function getSearchSettings(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/search-settings`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "검색 설정을 불러오지 못했습니다.");
  return data;
}

export async function resetAdminUserPassword(apiBase, token, targetUserid, newPassword) {
  const response = await fetch(`${apiBase}/admin/users/${encodeURIComponent(targetUserid)}/reset-password`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ new_password: newPassword }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "비밀번호 초기화에 실패했습니다.");
  return data;
}

export async function verifyAdminUserContact(apiBase, token, targetUserid, payload = {}) {
  const response = await fetch(`${apiBase}/admin/users/${encodeURIComponent(targetUserid)}/verify-contact`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({
      email_verified: payload.email_verified ?? true,
      phone_verified: payload.phone_verified ?? true,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "연락처 인증 처리에 실패했습니다.");
  return data;
}

export async function deleteAdminUser(apiBase, token, targetUserid) {
  const response = await fetch(`${apiBase}/admin/users/${encodeURIComponent(targetUserid)}`, {
    method: "DELETE",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "회원 탈퇴 처리에 실패했습니다.");
  return data;
}

export async function updateSearchSettings(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/search-settings`, {
    method: "PATCH",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "검색 설정 저장에 실패했습니다.");
  return data;
}

export async function testSearchSettings(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/search-settings/test`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "검색 테스트에 실패했습니다.");
  return data;
}

export async function getWeatherSettings(apiBase, token) {
  const response = await fetch(`${apiBase}/admin/weather/settings`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "날씨 설정을 불러오지 못했습니다.");
  return data;
}

export async function updateWeatherSettings(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/weather/settings`, {
    method: "PATCH",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "날씨 설정 저장에 실패했습니다.");
  return data;
}

export async function testWeatherSettings(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/admin/weather/test`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "날씨 테스트에 실패했습니다.");
  return data;
}
export async function fetchClientSyncSummary(apiBase, token, tenantId = "") {
  const query = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const response = await fetch(`${apiBase}/api/client-sync/admin/summary${query}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client sync summary could not be loaded.");
  return data;
}
export async function runClientSyncSelfTest(apiBase, token, tenantId = "tobe-demo") {
  const response = await fetch(`${apiBase}/api/client-sync/admin/self-test`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ tenant_id: tenantId || "tobe-demo" }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client sync self-test failed.");
  return data;
}
export async function fetchClientSyncEvents(apiBase, token, tenantId = "", limit = 80) {
  const params = new URLSearchParams();
  if (tenantId) params.set("tenant_id", tenantId);
  if (limit) params.set("limit", String(limit));
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await fetch(`${apiBase}/api/client-sync/admin/events${query}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client sync events could not be loaded.");
  return data;
}
export async function fetchClientRegistrationTokens(apiBase, token, tenantId = "") {
  const query = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const response = await fetch(`${apiBase}/api/client-sync/admin/registration-tokens${query}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client registration tokens could not be loaded.");
  return data;
}

export async function createClientRegistrationToken(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/api/client-sync/admin/registration-tokens`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client registration token could not be created.");
  return data;
}

export async function revokeClientRegistrationToken(apiBase, token, tokenId) {
  const response = await fetch(`${apiBase}/api/client-sync/admin/registration-tokens/${tokenId}/revoke`, {
    method: "POST",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Client registration token could not be revoked.");
  return data;
}
export async function runClientConnectionSmokeTest(apiBase, registrationToken) {
  const deviceId = `admin-browser-smoke-${Date.now()}`;
  const platform = typeof navigator !== "undefined" ? navigator.userAgent : "admin-browser";

  const registerResponse = await fetch(`${apiBase}/api/client-auth/register-device`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      registration_token: registrationToken,
      device_id: deviceId,
      device_type: "browser-test",
      client_name: "Admin browser connection test",
      platform,
      local_model: "admin-smoke-test",
      capabilities: {
        offline_first: true,
        event_upload: true,
        learning_upload: true,
        pull_learning: true,
      },
    }),
  });
  const registerData = await registerResponse.json().catch(() => ({}));
  if (!registerResponse.ok) throw new Error(registerData.detail || "Client device registration failed.");

  const clientToken = registerData.client_token;
  const authHeaders = { Authorization: `Bearer ${clientToken}`, "Content-Type": "application/json" };

  const eventResponse = await fetch(`${apiBase}/api/client-sync/token/events`, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify({
      events: [
        {
          event_type: "admin-browser-smoke",
          event_time: new Date().toISOString(),
          message: "Browser smoke test event upload succeeded.",
        },
      ],
    }),
  });
  const eventData = await eventResponse.json().catch(() => ({}));
  if (!eventResponse.ok) throw new Error(eventData.detail || "Client event upload failed.");

  const bundleResponse = await fetch(`${apiBase}/api/client-sync/token/learning-bundles`, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify({
      bundle_type: "admin-browser-smoke",
      summary: "관리자 브라우저에서 실행한 클라이언트 연결 테스트 학습 번들입니다.",
      content_hash: `admin-browser-smoke-${deviceId}`,
      payload: {
        source: "admin-browser-smoke",
        device_id: deviceId,
        created_at: new Date().toISOString(),
      },
    }),
  });
  const bundleData = await bundleResponse.json().catch(() => ({}));
  if (!bundleResponse.ok) throw new Error(bundleData.detail || "Client learning bundle upload failed.");

  const pullResponse = await fetch(`${apiBase}/api/client-sync/token/pull`, {
    method: "GET",
    headers: { Authorization: `Bearer ${clientToken}` },
  });
  const pullData = await pullResponse.json().catch(() => ({}));
  if (!pullResponse.ok) throw new Error(pullData.detail || "Client pull failed.");

  return {
    status: "ok",
    tenant_id: registerData.tenant_id,
    device_id: deviceId,
    registered: true,
    event_received: eventData.received || 0,
    bundle_id: bundleData.bundle_id,
    pulled_bundles: (pullData.learning_bundles || []).length,
    manifest: pullData.manifest || registerData.manifest,
  };
}

export async function fetchThemeTemplates(apiBase, token) {
  const response = await fetch(`${apiBase}/api/theme-templates`, {
    method: "GET",
    headers: token ? makeAuthHeaders(token) : {},
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "템플릿 목록을 불러오지 못했습니다.");
  return data;
}

export async function createTenant(apiBase, token, payload) {
  const response = await fetch(`${apiBase}/api/tenants`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "회사 AI 작업공간을 생성하지 못했습니다.");
  return data;
}

export async function fetchTenant(apiBase, token, tenantId) {
  const response = await fetch(`${apiBase}/api/tenants/${encodeURIComponent(tenantId)}`, {
    method: "GET",
    headers: makeAuthHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "회사 AI 설정을 불러오지 못했습니다.");
  return data;
}

export async function updateTenantBranding(apiBase, token, tenantId, payload) {
  const response = await fetch(`${apiBase}/api/tenants/${encodeURIComponent(tenantId)}/branding`, {
    method: "PUT",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "브랜드 설정을 저장하지 못했습니다.");
  return data;
}

export async function updateTenantAiProfile(apiBase, token, tenantId, payload) {
  const response = await fetch(`${apiBase}/api/tenants/${encodeURIComponent(tenantId)}/ai-profile`, {
    method: "PUT",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "AI 프로필을 저장하지 못했습니다.");
  return data;
}

export async function updateTenantModules(apiBase, token, tenantId, payload) {
  const response = await fetch(`${apiBase}/api/tenants/${encodeURIComponent(tenantId)}/modules`, {
    method: "PUT",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "사용 모듈을 저장하지 못했습니다.");
  return data;
}

export async function generateTenantAi(apiBase, token, tenantId) {
  const response = await fetch(`${apiBase}/api/tenants/${encodeURIComponent(tenantId)}/generate-ai`, {
    method: "POST",
    headers: { ...makeAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "회사 AI 생성 후처리를 완료하지 못했습니다.");
  return data;
}

export async function resolveTenant(apiBase, host) {
  const response = await fetch(`${apiBase}/api/tenant-resolve?host=${encodeURIComponent(host)}`, {
    method: "GET",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "도메인 연결을 확인하지 못했습니다.");
  return data;
}
